import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { useModal } from '@/components/modal/useModal';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/popover/HoverCard';
import { Action, Card } from '@/components/sections/Card';
import SiBookOpen from '@/icons/SiBookOpen';
import SiContacts from '@/icons/SiContacts';
import SiDownload from '@/icons/SiDownload';
import SiEdit from '@/icons/SiEdit';
import SiLightBulb from '@/icons/SiLightBulb';
import SiLightOff from '@/icons/SiLightOff';
import { FiSettings } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import { HardwareDescription, hardwareDescription } from '@/pages/onboarding/company/importHardwareHelper';
import { ApprovalStatus, Company, ContractStatus, Hardware } from '@/services/api/contracts/companies';
import { NotFoundError } from '@/services/api/errors/APIError';
import { useEnableCustomer, useGetCustomerContract, useGetCustomerNda } from '@/services/api/hooks/companies';
import { useCustomerCreditCheck } from '@/services/api/hooks/credit';
import { useDownloadStorageItem } from '@/services/api/hooks/images';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import useCSVDownload from '@/services/csv/useCSVDownload';
import { usePrintCurrencyStringForCurrentMsp } from '@/utils/price';
import { FunctionComponent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import DisableCustomerModal from './DisableCustomerModal';
import EditCustomerModal from './EditCustomerModal';
import { getCustomerStatus } from './helper';
import InviteUsersToCustomerModal from './InviteUsersToCustomerModal';
import ShowCustomerAgentsModal from './ShowCustomerAgentsModal';

interface CustomerCardProps {
  customer: Company;
  hardware: Hardware | undefined | null;
}

const isCustomerOnboarded = (customer: Company) => {
  return Boolean(
    customer.contractStatus == ContractStatus.ContractSigned && Object.keys(customer.registeredInfo).length !== 0
  );
};

type StatusInfo = {
  icon: JSX.Element;
  card: JSX.Element;
};

export const PendingInviteInfoCard: FunctionComponent = () => {
  return (
    <>
      <h3 className="text-primary-std font-medium">Customer invite is pending</h3>
      <p className="text-secondary-std text-sm leading-tight">
        This customer is pending approval to invite a customer representative. Click here to change customer status to
        Invite Approved.
      </p>
    </>
  );
};

export const PendingApprovalInfoCard: FunctionComponent = () => {
  return (
    <>
      <h3 className="text-primary-std font-medium">Customer approval is pending</h3>
      <p className="text-secondary-std text-sm leading-tight">
        This customer is pending approval to to begin onboarding. Click here to approve customer access level and begin
        onboarding.
      </p>
    </>
  );
};

const editHoverIconRed = (
  <svg className="text-xl text-red-600 cursor-pointer" width="1em" height="1em" fill="none" viewBox="0 0 24 24">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4.75 19.25L9 18.25L18.2929 8.95711C18.6834 8.56658 18.6834 7.93342 18.2929 7.54289L16.4571 5.70711C16.0666 5.31658 15.4334 5.31658 15.0429 5.70711L5.75 15L4.75 19.25Z"
    />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.25 19.25H13.75" />
  </svg>
);

export const StatusInfoCard = (customer: Company) => {
  let statusInfo: StatusInfo | null = null;
  if (customer.contractStatus > ContractStatus.Pending && customer.approvalStatus === ApprovalStatus.InviteApproved) {
    statusInfo = {
      icon: editHoverIconRed,
      card: <PendingApprovalInfoCard />
    };
  } else if (customer.approvalStatus === ApprovalStatus.Pending) {
    statusInfo = {
      icon: editHoverIconRed,
      card: <PendingInviteInfoCard />
    };
  }
  return statusInfo;
};

const CustomerCard: FunctionComponent<CustomerCardProps> = ({ customer, hardware }) => {
  const navigate = useNavigate();
  const role = useRole();
  const { partnerId } = useParams<'partnerId'>();
  if (!partnerId) throw new Error('Missing partnerId');

  const disableCustomerModalProps = useModal<{ mspId: string; customerId: string }>();
  const editCustomerModalProps = useModal<{ mspId: string; customerId: string }>();
  const inviteCustomerModalProps = useModal<{ mspId: string; customerId: string }>();
  const showAgentsModalProps = useModal<{ mspId: string; customerId: string }>();

  const onCustomerEdit = () => editCustomerModalProps.open({ mspId: partnerId, customerId: customer.id });
  const onCustomerDisable = () => disableCustomerModalProps.open({ mspId: partnerId, customerId: customer.id });
  const onCustomerManageUsers = () => inviteCustomerModalProps.open({ mspId: partnerId, customerId: customer.id });
  const onCustomerAgents = () => showAgentsModalProps.open({ mspId: partnerId, customerId: customer.id });

  const getContract = useGetCustomerContract();
  const getNda = useGetCustomerNda();
  const { downloadCSV } = useCSVDownload();

  const downloadStorageItem = useDownloadStorageItem();
  const downloadContract = async () => {
    try {
      const contract = await getContract.mutateAsync({ partnerId, id: customer.id });
      const url = await downloadStorageItem.mutateAsync({ path: contract.signedDocumentPath });
      window.open(url, '_blank');
    } catch (e) {
      if (e instanceof NotFoundError) {
        toast.error('The contract could not be found');
        return;
      }
      toast.error('The contract could not be downloaded');
    }
  };
  const downloadNda = async () => {
    try {
      const nda = await getNda.mutateAsync({ partnerId, id: customer.id });
      const url = await downloadStorageItem.mutateAsync({ path: nda.signedDocumentPath });
      window.open(url, '_blank');
    } catch (e) {
      if (e instanceof NotFoundError) {
        toast.error('The NDA could not be found');
        return;
      }
      toast.error('The NDA could not be downloaded');
    }
  };
  const downloadHardware = async () => {
    try {
      downloadCSV(
        `${customer.name}-hardware`,
        hardwareDescription,
        hardware?.manifest.data?.map(row =>
          hardwareDescription.reduce<HardwareDescription>(
            (acc, curr) => ((acc[curr as keyof HardwareDescription] = row[curr]), acc),
            {}
          )
        ) || []
      );
    } catch (e) {
      if (e instanceof NotFoundError) {
        toast.error('The hardware manifest could not be found');
        return;
      }
      toast.error('The hardware manifest could not be downloaded');
    }
  };

  const onCatalogueClick = () => {
    navigate(`/msps/${partnerId}/companies/${customer.id}/catalogue`);
  };

  const onPurchaseOrderClick = () => {
    navigate(`/msps/${partnerId}/companies/${customer.id}/purchaseOrders`);
  };

  const onNotificationsClick = () => {
    navigate(`/msps/${partnerId}/companies/${customer.id}/settings/notifications`);
  };

  const enableCustomer = useEnableCustomer();
  const onEnable = async () => {
    await enableCustomer.mutateAsync({
      partnerId,
      id: customer.id
    });
    toast.success(`${customer.name} enabled`);
  };

  const canManageUsers = !customer.disabled;
  const showAuthorised = role.type !== RoleType.Company && customer.contractStatus === ContractStatus.ContractSigned;
  const canDownloadHardware = role.type === RoleType.Admin;
  const statusInfo = StatusInfoCard(customer);

  const [creditCheck] = useCustomerCreditCheck(partnerId, customer.id, customer.customerCreditCheckId);
  const printCurrencyString = usePrintCurrencyStringForCurrentMsp();

  return (
    <>
      <EditCustomerModal {...editCustomerModalProps} />
      <DisableCustomerModal {...disableCustomerModalProps} />
      <InviteUsersToCustomerModal {...inviteCustomerModalProps} />
      <ShowCustomerAgentsModal {...showAgentsModalProps} />
      <Card className="h-fit">
        <div className={cn('flex flex-col p-4', customer.disabled && 'opacity-60')}>
          <div className="flex space-x-2 justify-between">
            <h4 className="font-bold text-2xl flex-1">{customer.name}</h4>
            {!statusInfo && (
              <Button variant={ButtonVariant.Ghost} onClick={onCustomerEdit}>
                <SiEdit className="text-xl text-green-600" />
              </Button>
            )}
            {statusInfo && (
              <HoverCard>
                <Button variant={ButtonVariant.Ghost} onClick={onCustomerEdit}>
                  <HoverCardTrigger asChild>{statusInfo.icon}</HoverCardTrigger>
                </Button>
                <HoverCardContent className="flex flex-col gap-1">{statusInfo.card}</HoverCardContent>
              </HoverCard>
            )}
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>NDA</span>
            <span className="text-primary-600 font-semibold">
              {(() => {
                switch (customer.contractStatus) {
                  case ContractStatus.Applied:
                    return 'Applied';
                  case ContractStatus.ContractSigned:
                  case ContractStatus.NdaSigned:
                    return 'Signed';
                  default:
                    return 'Unsigned';
                }
              })()}
            </span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Terms & Conditions</span>
            <span className="text-primary-600 font-semibold">
              {(() => {
                switch (customer.contractStatus) {
                  case ContractStatus.Applied:
                  case ContractStatus.NdaSigned:
                    return 'Applied';
                  case ContractStatus.ContractSigned:
                    return 'Signed';
                  default:
                    return 'Unsigned';
                }
              })()}
            </span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Onboarded</span>
            <span className="text-primary-600 font-semibold">{isCustomerOnboarded(customer) ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Status</span>
            <span className="text-primary-600 font-semibold">{getCustomerStatus(customer.approvalStatus)}</span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Credit Limit</span>
            <span className="text-primary-600 font-semibold">
              {creditCheck !== null ? printCurrencyString(creditCheck.creditLimit * 100) : '-'}
            </span>
          </div>
        </div>
        <div className="flex flex-col">
          <Action onClick={onCatalogueClick} className="flex items-center justify-center gap-1">
            <SiBookOpen className="text-xl" />
            <span>Catalogue</span>
          </Action>
          <Action onClick={onPurchaseOrderClick} className="flex items-center justify-center gap-1">
            <SiBookOpen className="text-xl" />
            <span>Orders / Quotes</span>
          </Action>
          {customer.enableCustomNotifications && (
            <Action onClick={onNotificationsClick} className="flex items-center justify-center gap-1">
              <FiSettings className="text-xl" />
              <span>Notification Preferences</span>
            </Action>
          )}
          {canManageUsers && (
            <Action
              onClick={onCustomerManageUsers}
              disabled={customer.disabled}
              className="flex items-center justify-center gap-1"
            >
              <SiEdit className="text-xl" />
              <span>Invite customer</span>
            </Action>
          )}
          {showAuthorised && (
            <Action
              onClick={onCustomerAgents}
              disabled={customer.disabled}
              className="flex items-center justify-center gap-1"
            >
              <SiContacts className="text-xl" />
              <span>Show authorised agents</span>
            </Action>
          )}
          {customer.contractStatus >= ContractStatus.NdaSigned && (
            <Action onClick={downloadNda} className="flex items-center justify-center gap-1">
              <SiDownload className="text-xl" />
              <span>Download NDA</span>
            </Action>
          )}
          {customer.contractStatus == ContractStatus.ContractSigned && (
            <Action onClick={downloadContract} className="flex items-center justify-center gap-1">
              <SiDownload className="text-xl" />
              <span>Download contract</span>
            </Action>
          )}
          {canDownloadHardware && hardware && (
            <Action onClick={downloadHardware} className="flex items-center justify-center gap-1">
              <SiDownload className="text-xl" />
              <span>Download hardware</span>
            </Action>
          )}
          {!customer.disabled && (
            <Action onClick={onCustomerDisable} className="flex items-center justify-center gap-1 text-red-600">
              <SiLightOff className="text-xl" />
              <span>Disable</span>
            </Action>
          )}
          {customer.disabled && (
            <Action
              loading={enableCustomer.isLoading}
              onClick={onEnable}
              className="flex items-center justify-center gap-1 text-green-600"
            >
              <SiLightBulb className="text-xl" />
              <span>Enable</span>
            </Action>
          )}
        </div>
      </Card>
    </>
  );
};

export default CustomerCard;
